package at.theatervogelweide.markdown

import org.intellij.markdown.IElementType
import org.intellij.markdown.MarkdownElementTypes
import org.intellij.markdown.MarkdownTokenTypes
import org.intellij.markdown.ast.ASTNode
import org.intellij.markdown.ast.LeafASTNode
import org.intellij.markdown.ast.accept
import org.intellij.markdown.flavours.gfm.GFMFlavourDescriptor
import org.intellij.markdown.html.GeneratingProvider
import org.intellij.markdown.html.HtmlGenerator
import org.intellij.markdown.html.SimpleTagProvider
import org.intellij.markdown.html.TrimmingInlineHolderProvider
import org.intellij.markdown.html.URI
import org.intellij.markdown.html.entities.Entities
import org.intellij.markdown.parser.CancellationToken
import org.intellij.markdown.parser.LinkMap
import org.intellij.markdown.parser.MarkdownParser

/**
 * GitHub-flavoured Markdown with the public site's differences from the standard (marked with `breaks: true`, after
 * escaping `<`): a single line break stays a line break, and raw HTML shows as text. Unsafe links such as
 * `javascript:` are dropped. Headings are one level lower, `#` an `h2`, as the page's title is its `h1`, and
 * `###` and deeper are all `h4` (public design system, Prose).
 */
private object PublicSiteFlavour : GFMFlavourDescriptor(useSafeLinks = true) {
    override fun createHtmlGeneratingProviders(linkMap: LinkMap, baseURI: URI?): Map<IElementType, GeneratingProvider> =
        super.createHtmlGeneratingProviders(linkMap, baseURI) + HEADINGS + (MarkdownElementTypes.PARAGRAPH to ParagraphWithLineBreaks)

    private val HEADINGS = mapOf(
        MarkdownElementTypes.ATX_1 to SimpleTagProvider("h2"),
        MarkdownElementTypes.ATX_2 to SimpleTagProvider("h3"),
        MarkdownElementTypes.ATX_3 to SimpleTagProvider("h4"),
        MarkdownElementTypes.ATX_4 to SimpleTagProvider("h4"),
        MarkdownElementTypes.ATX_5 to SimpleTagProvider("h4"),
        MarkdownElementTypes.ATX_6 to SimpleTagProvider("h4"),
        MarkdownElementTypes.SETEXT_1 to SimpleTagProvider("h2"),
        MarkdownElementTypes.SETEXT_2 to SimpleTagProvider("h3"),
    )
}

private object ParagraphWithLineBreaks : TrimmingInlineHolderProvider() {
    override fun openTag(visitor: HtmlGenerator.HtmlGeneratingVisitor, text: String, node: ASTNode) {
        visitor.consumeTagOpen(node, "p")
    }

    override fun closeTag(visitor: HtmlGenerator.HtmlGeneratingVisitor, text: String, node: ASTNode) {
        visitor.consumeTagClose("p")
    }

    override fun processNode(visitor: HtmlGenerator.HtmlGeneratingVisitor, text: String, node: ASTNode) {
        openTag(visitor, text, node)
        for (child in childrenToRender(node)) {
            when {
                child.type == MarkdownTokenTypes.EOL -> visitor.consumeHtml("<br />")
                child is LeafASTNode -> visitor.visitLeaf(child)
                else -> child.accept(visitor)
            }
        }
        closeTag(visitor, text, node)
    }
}

/** The HTML the public site shows for [markdown], inside `<body>`. */
fun renderMarkdown(markdown: String): String {
    // the parser expects LF only; texts from MCP clients or imports may have CRLF or CR
    val escaped = markdown.replace("\r\n", "\n").replace('\r', '\n').replace("<", "&lt;")
    val tree = MarkdownParser(PublicSiteFlavour, assertionsEnabled = false, CancellationToken.NonCancellable)
        .buildMarkdownTreeFromString(escaped as CharSequence)
    return HtmlGenerator(escaped, tree, PublicSiteFlavour).generateHtml()
}

/**
 * A part of a Markdown text as [renderMarkdown] reads it: its [type], the parser's name such as `INLINE_LINK`,
 * `LIST_BULLET`, `STRONG` or `ATX_1`, where it is in the text, and its [children]. A [leaf] is one of the parser's
 * tokens, such as the `**` of `STRONG`, which may share its name with a part (`EMPH`).
 */
class MarkdownNode(val type: String, val start: Int, val end: Int, val leaf: Boolean, val children: List<MarkdownNode>)

/**
 * [markdown] as [renderMarkdown] reads it, with offsets into [markdown] itself, for tools that change the Markdown
 * rather than show it, such as the admin's formatting toolbar (#166). Like the site, it reads `<` as text; [markdown]
 * should have LF line ends.
 */
fun parseMarkdown(markdown: String): MarkdownNode {
    // punctuation reads as text like the site's `&lt;` does and keeps the offsets; one the text doesn't have, so a `<`
    // in a link label doesn't match a label with that character
    val stand = LESS_THAN_STANDS.firstOrNull { it !in markdown } ?: '&'
    val text = markdown.replace('<', stand).replace('\r', ' ')
    val tree = MarkdownParser(PublicSiteFlavour, assertionsEnabled = false, CancellationToken.NonCancellable)
        .buildMarkdownTreeFromString(text as CharSequence)
    return tree.toMarkdownNode()
}

// punctuation the club's texts are unlikely to have
private const val LESS_THAN_STANDS = "※‽⁂⁑⁕⸎⸏⸐⸑⸓⸙§¶†‡&"

private fun ASTNode.toMarkdownNode(): MarkdownNode =
    MarkdownNode(type.name, startOffset, endOffset, this is LeafASTNode, children.map { it.toMarkdownNode() })

/**
 * [markdown] as plain text on one line, for teasers and link previews: the text [renderMarkdown] shows, without
 * markup, line breaks and block boundaries as spaces.
 */
fun markdownToText(markdown: String): String =
    renderMarkdown(markdown)
        // every `<` in the text is escaped, so whatever is between `<` and `>` is a tag the generator wrote
        .replace(Regex("""<br />|</(p|h[1-6]|li|blockquote|pre|tr|td|th)>"""), " ")
        .replace(Regex("""<[^>]*>"""), "")
        .let(::decodeEntities)
        // ASCII whitespace only, on every platform: JavaScript's \s would also take a no-break space from `&nbsp;`
        .replace(Regex("[ \t\n\r\u000c]+"), " ")
        .trim()

/**
 * Entity and numeric character references as CommonMark reads them: `&#` with 1–7 decimal digits, `&#x` or `&#X` with
 * 1–6 hexadecimal ones, or an HTML5 entity name, which may hold digits after its first letter (`&frac12;`).
 */
private val ENTITY = Regex("""&(?:#([0-9]{1,7})|#[xX]([0-9a-fA-F]{1,6})|([a-zA-Z][a-zA-Z0-9]*));""")

private fun decodeEntities(html: String): String = ENTITY.replace(html) { match ->
    val (decimal, hexadecimal, name) = match.destructured
    when {
        decimal.isNotEmpty() -> codePointText(decimal.toInt())
        hexadecimal.isNotEmpty() -> codePointText(hexadecimal.toInt(16))
        // every named entity of HTML5, such as `&euro;` or `&hellip;`; unknown names stay as written
        else -> Entities.map[match.value]?.let(::codePointText) ?: match.value
    }
}

/** [codePoint] as text; like CommonMark, U+FFFD for 0, surrogates and anything beyond Unicode. */
private fun codePointText(codePoint: Int): String = when (codePoint) {
    in 1..0xD7FF, in 0xE000..0xFFFF -> Char(codePoint).toString()
    in 0x10000..0x10FFFF -> (codePoint - 0x10000).let { charArrayOf(Char(0xD800 + (it shr 10)), Char(0xDC00 + (it and 0x3FF))).concatToString() }
    else -> "\uFFFD"
}
